import React, { useState, useRef, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  EmailOutlined as EmailIcon,
  LockOutlined as LockIcon,
  Login as LoginIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import ReCAPTCHA from 'react-google-recaptcha';
import authService from '../../services/auth';
import { useNavigate } from 'react-router-dom';
import aptiviewLogo from '../../assets/aptiview-logo.png';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();

  // Interactive Particle Constellation Canvas Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    const mouse = { x: -1000, y: -1000, radius: 140 };
    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const handleMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    // Generate responsive particles
    const particleCount = Math.min(55, Math.max(25, Math.floor((width * height) / 24000)));
    const palette = [
      'rgba(59, 130, 246, ', // blue
      'rgba(14, 165, 233, ', // sky
      'rgba(99, 102, 241, ', // indigo
      'rgba(56, 189, 248, ', // cyan
    ];

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      baseAlpha: number;
      color: string;
    }> = [];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.65,
        vy: (Math.random() - 0.5) * 0.65,
        size: Math.random() * 2.2 + 1.2,
        baseAlpha: Math.random() * 0.45 + 0.35,
        color: palette[Math.floor(Math.random() * palette.length)],
      });
    }

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw dynamic web lines between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 125) {
            const lineAlpha = (1 - dist / 125) * 0.24;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(99, 102, 241, ${lineAlpha})`;
            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw & update particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        p.x += p.vx;
        p.y += p.vy;

        // Bounce gently off boundaries
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        // Subtle cursor attraction / repulsion
        const mdx = mouse.x - p.x;
        const mdy = mouse.y - p.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < mouse.radius) {
          const force = (1 - mdist / mouse.radius) * 1.6;
          p.x -= (mdx / mdist) * force;
          p.y -= (mdy / mdist) * force;
        }

        // Draw particle node
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${p.baseAlpha})`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(59, 130, 246, 0.6)';
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Attempting login...');
      const response = await authService.login({
        ...formData,
        recaptcha_response: recaptchaToken || 'no_recaptcha',
      });

      console.log('Login response received:', response);

      if (!response.is_superuser) {
        setError('Access denied. Only super admin users can access this panel.');
        authService.logout();
        return;
      }

      console.log('User is super admin, calling onLoginSuccess...');
      onLoginSuccess();

      setTimeout(() => {
        console.log('Redirecting to dashboard...');
        navigate('/dashboard');
      }, 100);
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Login failed. Please check credentials and try again.');
      }
    } finally {
      setLoading(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken('');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token || '');
  };

  const handleRecaptchaError = () => {
    setError('reCAPTCHA failed to load. Check your connection or disable ad-blockers.');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0F1D', // Deep midnight slate foundation
        overflow: 'hidden',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* -----------------------------------------------------------------
          Layer 1: Floating Glowing Aurora Mesh Orbs
          ----------------------------------------------------------------- */}
      <Box
        sx={{
          position: 'absolute',
          top: '-15%',
          left: '-10%',
          width: { xs: 380, sm: 550, md: 650 },
          height: { xs: 380, sm: 550, md: 650 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37, 99, 235, 0.45) 0%, rgba(37, 99, 235, 0) 70%)',
          filter: 'blur(85px)',
          animation: 'floatOrb1 20s ease-in-out infinite alternate',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '-12%',
          width: { xs: 340, sm: 500, md: 600 },
          height: { xs: 340, sm: 500, md: 600 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(14, 165, 233, 0.42) 0%, rgba(14, 165, 233, 0) 70%)',
          filter: 'blur(80px)',
          animation: 'floatOrb2 25s ease-in-out infinite alternate',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-18%',
          left: '15%',
          width: { xs: 360, sm: 580, md: 680 },
          height: { xs: 360, sm: 580, md: 680 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.38) 0%, rgba(99, 102, 241, 0) 70%)',
          filter: 'blur(95px)',
          animation: 'floatOrb3 22s ease-in-out infinite alternate',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '38%',
          left: '45%',
          width: { xs: 300, sm: 460, md: 520 },
          height: { xs: 300, sm: 460, md: 520 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.28) 0%, rgba(6, 182, 212, 0) 70%)',
          filter: 'blur(80px)',
          animation: 'floatOrb4 24s ease-in-out infinite alternate',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* -----------------------------------------------------------------
          Layer 2: Cyber Dot-Grid Matrix Texture
          ----------------------------------------------------------------- */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 1.2px, transparent 1.2px)',
          backgroundSize: '32px 32px',
          animation: 'gridSweep 80s linear infinite',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* -----------------------------------------------------------------
          Layer 3: Interactive Constellation Particle Canvas
          ----------------------------------------------------------------- */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* -----------------------------------------------------------------
          Layer 4: Frosted Glassmorphism Login Card
          ----------------------------------------------------------------- */}
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
        <Card
          sx={{
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(24px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.75)',
            boxShadow: '0 30px 80px -15px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
            overflow: 'hidden',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              boxShadow: '0 35px 90px -15px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(255, 255, 255, 0.4) inset',
            },
          }}
        >
          {/* Subtle Top Accent Gradient Line */}
          <Box
            sx={{
              height: 4,
              width: '100%',
              background: 'linear-gradient(90deg, #1E3A8A 0%, #3B82F6 50%, #0EA5E9 100%)',
            }}
          />

          <CardContent sx={{ p: { xs: 3.5, sm: 4.5 } }}>
            {/* Header / Brand Logo */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                <Box
                  component="img"
                  src={aptiviewLogo}
                  alt="AptiView"
                  sx={{
                    height: 52,
                    maxWidth: 240,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 14px rgba(37, 99, 235, 0.2))',
                  }}
                />
              </Box>

              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  icon={<SecurityIcon sx={{ fontSize: '13px !important' }} />}
                  label="Super Admin Portal"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    height: 22,
                    backgroundColor: 'var(--primary-50)',
                    color: 'var(--primary-700)',
                    border: '1px solid var(--primary-100)',
                  }}
                />
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Sign in to access centralized controls, tenant metrics, and system operations.
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2.5,
                  borderRadius: '12px',
                  fontSize: '0.825rem',
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@aptiview.ai"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" sx={{ color: 'var(--primary-600)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" sx={{ color: 'var(--primary-600)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                        sx={{ color: 'var(--neutral-400)' }}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2.5 }}
              />

              {RECAPTCHA_SITE_KEY && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onErrored={handleRecaptchaError}
                  />
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                disabled={loading || (!!RECAPTCHA_SITE_KEY && !recaptchaToken)}
                sx={{
                  py: 1.4,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 50%, #3B82F6 100%)',
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                    boxShadow: '0 8px 22px rgba(37, 99, 235, 0.55)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                {loading ? 'Authenticating...' : 'Sign In to Super Admin'}
              </Button>

              {/* Bottom Security Footer */}
              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ mt: 3, opacity: 0.7 }}>
                <SecurityIcon sx={{ fontSize: 13, color: 'var(--neutral-500)' }} />
                <Typography variant="caption" sx={{ color: 'var(--neutral-500)', fontSize: '0.725rem' }}>
                  Enterprise TLS Encryption & Audit Verification
                </Typography>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
